import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wand2, RefreshCw, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  fetchStylePreferences,
  deleteStylePreferences,
} from "../lib/api";
import { track } from "../lib/analytics";

/**
 * StylePreferencesCard — Settings entry point for editing the saved
 * onboarding answers (goal, coverage, finishes, ethics, etc).
 *
 * The card has two affordances:
 *   1. "Refresh my answers" → routes to `/onboarding?edit=1` which
 *      forces the full flow even when saved prefs exist.
 *   2. "Clear saved answers" → wipes the stored blob so the next
 *      "new look" lands the user back in onboarding step 0.
 */
const PREF_LABELS = {
  goal: "Vibe",
  experience: "Experience",
  coverage: "Coverage",
  texture: "Texture",
  lip_finish: "Lip finish",
  eye_finish: "Eye finish",
  beard_shadow: "Beard-shadow step",
  ethics: "Ethics",
  skip_categories: "Skip categories",
};

const fmtValue = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return v ? "On" : "Off";
  if (Array.isArray(v)) {
    if (v.length === 0) return null;
    return v.map((x) => String(x).replace(/_/g, " ")).join(", ");
  }
  return String(v).replace(/_/g, " ");
};

const StylePreferencesCard = () => {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchStylePreferences();
        if (!cancelled) {
          setPrefs(data?.preferences || null);
          setUpdatedAt(data?.updated_at || null);
        }
      } catch {
        if (!cancelled) setPrefs(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onForget = async () => {
    if (busy) return;
    if (
      !window.confirm(
        "Clear your saved style answers? You'll be asked all 11 questions again next time you start a new look.",
      )
    )
      return;
    setBusy(true);
    try {
      await deleteStylePreferences();
      track("style_preferences_cleared");
      setPrefs(null);
      setUpdatedAt(null);
      toast.success("Cleared. We'll ask fresh next time.");
    } catch {
      toast.error("Couldn't clear. Try again in a sec.");
    } finally {
      setBusy(false);
    }
  };

  const onRefresh = () => {
    track("style_preferences_refresh_clicked");
    navigate("/onboarding?edit=1");
  };

  const summary =
    prefs &&
    Object.entries(prefs)
      .filter(([k]) => PREF_LABELS[k])
      .map(([k, v]) => [PREF_LABELS[k], fmtValue(v)])
      .filter(([, v]) => v !== null);

  return (
    <div
      className="bg-white/80 backdrop-blur-xl border border-[#EADDD7] rounded-3xl p-7 soft-shadow mb-5"
      data-testid="style-preferences-card"
    >
      <div className="flex items-start gap-4 mb-5">
        <div className="shrink-0 h-11 w-11 rounded-full bg-[#F2E8E9] flex items-center justify-center">
          <Wand2 className="h-5 w-5 text-[#9B6A6C]" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-medium text-[#2C2123] leading-tight mb-1">
            Your style answers
          </h3>
          <p className="text-sm text-[#5C4B51] max-w-xl">
            We remember your onboarding so you don't have to re-answer 11
            questions every time. Refresh them whenever your taste shifts.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#9B6A6C]">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : !prefs || (summary && summary.length === 0) ? (
        <div className="bg-[#FDF6F3] border border-[#F2E8E9] rounded-xl p-4 text-sm text-[#5C4B51]">
          You haven't saved any style answers yet — finish onboarding once
          and we'll keep them here for next time.
        </div>
      ) : (
        <>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5"
            data-testid="style-preferences-summary"
          >
            {summary.map(([label, value]) => (
              <div
                key={label}
                className="bg-white border border-[#EADDD7] rounded-xl px-3 py-2.5"
              >
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#9B6A6C] mb-0.5">
                  {label}
                </div>
                <div className="text-sm text-[#2C2123] capitalize leading-snug">
                  {value}
                </div>
              </div>
            ))}
          </div>
          {updatedAt && (
            <div className="text-[11px] text-[#8A7D82] mb-4">
              Saved{" "}
              {new Date(updatedAt).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </div>
          )}
        </>
      )}

      <div className="flex flex-wrap gap-2 mt-2">
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex items-center gap-2 bg-[#9B6A6C] hover:bg-[#7d5455] text-white rounded-full px-4 py-2 text-sm font-medium transition-colors"
          data-testid="style-preferences-refresh"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh my answers
        </button>
        {prefs && (
          <button
            type="button"
            onClick={onForget}
            disabled={busy}
            className="inline-flex items-center gap-2 bg-white border border-[#EADDD7] hover:border-[#9B6A6C]/40 text-[#5C4B51] hover:text-[#9B6A6C] rounded-full px-4 py-2 text-sm transition-colors disabled:opacity-50"
            data-testid="style-preferences-forget"
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            Clear saved answers
          </button>
        )}
      </div>
    </div>
  );
};

export default StylePreferencesCard;
